import React, { useEffect, useRef } from "react";

/**
 * Confetti — lightweight canvas-based burst.
 * Trigger via a global CustomEvent so any page/component can fire it:
 *   window.dispatchEvent(new CustomEvent("fife:confetti", { detail: { origin: "gufata_won" } }));
 *
 * Also exposes a helper: import { fireConfetti } from "@/components/Confetti".
 *
 * - No external dependency (pure canvas + rAF).
 * - Auto-cleans particles when they leave the viewport.
 * - Honours prefers-reduced-motion (silent no-op).
 */

export function fireConfetti(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent("fife:confetti", { detail }));
  } catch {}
}

const COLORS = [
  "#3b56e0", // brand blue
  "#22b5d4", // cyan
  "#f97316", // orange
  "#facc15", // gold
  "#a78bfa", // purple
  "#22c55e", // green
  "#ec4899", // pink
  "#ffffff",
];

function rand(min, max) { return Math.random() * (max - min) + min; }

export default function Confetti() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const spawn = (count = 160, origin = "center") => {
      if (reduce) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Emit from bottom-center + a slight second burst from the corners
      for (let i = 0; i < count; i++) {
        const fromCenter = i < count * 0.7;
        const startX = fromCenter ? w * 0.5 + rand(-40, 40) : (i % 2 === 0 ? 0 : w);
        const startY = fromCenter ? h - 20 : h * 0.6 + rand(-20, 20);
        const angle = fromCenter
          ? rand(-Math.PI * 0.75, -Math.PI * 0.25)
          : (startX < w * 0.5 ? rand(-Math.PI * 0.6, -Math.PI * 0.2) : rand(-Math.PI * 0.8, -Math.PI * 0.4));
        const speed = rand(9, 16);
        particlesRef.current.push({
          x: startX,
          y: startY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: rand(6, 11),
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          rot: rand(0, Math.PI * 2),
          vr: rand(-0.35, 0.35),
          shape: Math.random() < 0.5 ? "rect" : "circle",
          life: 0,
          maxLife: rand(120, 200),
          drag: rand(0.985, 0.995),
        });
      }
      if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
    };

    const tick = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);
      const particles = particlesRef.current;
      const gravity = 0.35;

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vy += gravity;
        p.vx *= p.drag;
        p.vy *= p.drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life += 1;
        const alpha = Math.max(0, 1 - (p.life / p.maxLife));
        if (alpha <= 0 || p.y > h + 40 || p.x < -40 || p.x > w + 40) {
          particles.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.55);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (particles.length > 0) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    const onFire = (e) => {
      const count = e?.detail?.count || 180;
      const origin = e?.detail?.origin || "";
      spawn(count, origin);
    };
    window.addEventListener("fife:confetti", onFire);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("fife:confetti", onFire);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      particlesRef.current = [];
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none z-[9999]"
      data-testid="confetti-canvas"
    />
  );
}
